// Time zones as the handoff has them (§9, "Store the zone, derive the
// offset"): an IANA name from the platform's own tzdata, never an offset or a
// label, and never hand-maintained. The offset and daylight saving are the
// server's reading of the zone now (lib/timezones.js zoneNow), shown beside it.

export interface ZoneNow {
  name: string
  abbrev: string
  is_dst: boolean
  utc_offset: string
}

// The browser's IANA list. The server checks every name against its own
// tzdata as well, so a name one side does not know is refused, not stored.
export function zoneNames(): string[] {
  const list: string[] = typeof (Intl as any).supportedValuesOf === 'function' ? (Intl as any).supportedValuesOf('timeZone') : []
  return list.includes('UTC') ? list : [...list, 'UTC']
}

// The names, with any the record already holds first so a select never
// drops a saved value the browser happens not to list.
export function zoneOptions(...held: (string | null | undefined)[]): string[] {
  const names = zoneNames()
  const extra = held.filter((z): z is string => !!z && !names.includes(z))
  return [...new Set(extra), ...names]
}

// A wall-clock reading in a zone; `mo` counts from 0, as Date's month does.
export interface Wall {
  y: number
  mo: number
  d: number
  h: number
  mi: number
  s: number
  ms: number
}

// The wall clock an instant shows in a zone, from the browser's tzdata. A
// zone this browser does not know reads as UTC, as the business clock does
// (pages/timesheets/time.ts).
export function wallOf(at: Date, zone: string): Wall {
  const read = (tz: string) =>
    Object.fromEntries(
      new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' })
        .formatToParts(at)
        .map((p) => [p.type, Number(p.value)]),
    )
  let p: Record<string, number>
  try {
    p = read(zone)
  } catch {
    p = read('UTC')
  }
  return { y: p.year, mo: p.month - 1, d: p.day, h: p.hour, mi: p.minute, s: p.second, ms: at.getUTCMilliseconds() }
}

const wallMs = (w: Wall) => Date.UTC(w.y, w.mo, w.d, w.h, w.mi, w.s, w.ms)

// The instant a wall-clock time names in a zone, read as Postgres reads
// `timestamp AT TIME ZONE zone`: a time the clocks skip, or one they show
// twice, takes the later of its two readings (standard time).
export function instantOf(w: Wall, zone: string): Date {
  const guess = wallMs(w)
  const offsetAt = (t: number) => wallMs(wallOf(new Date(t), zone)) - t
  const tries = [guess - offsetAt(guess - 86400000), guess - offsetAt(guess + 86400000)]
  const fits = tries.filter((t) => wallMs(wallOf(new Date(t), zone)) === guess)
  return new Date(Math.max(...(fits.length ? fits : tries)))
}

export const offsetText =(z: ZoneNow | null | undefined) =>
  z ? `${z.abbrev} · UTC${z.utc_offset} · daylight saving ${z.is_dst ? 'in effect' : 'not in effect'}` : ''
